import { useState } from 'react';
import { Download, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';

export function SarExportPanel({
  personId,
  fullName,
}: {
  personId: string;
  fullName: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Subject Access Request export (CLAUDE.md §10). Streams the
   * full JSON dossier as an attachment so the admin can hand it
   * to the data subject. We use fetch() directly rather than the
   * api() helper because the response is a file download, not a
   * JSON payload to parse.
   */
  async function download() {
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/people/${personId}/export`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `person-${personId}-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'export failed');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="flex items-center gap-2 border-b border-line/10 px-5 py-3">
        <Download className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold">Subject Access Request export</h2>
      </div>
      <div className="p-5 sm:p-6">
        <p className="text-xs text-fg-muted">
          Downloads everything the system holds about{' '}
          <strong>{fullName}</strong> as a JSON file: person record,
          metadata, full event chain, linked user account, and
          mobilisation attachments. Hand this to the data subject when
          they request a copy of their data (GDPR §15).
        </p>
        {error ? (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end border-t border-line/10 bg-surface-2 px-5 py-3">
        <Button
          variant="secondary"
          size="md"
          iconLeft={<Download className="h-4 w-4" />}
          loading={downloading}
          onClick={download}
        >
          Download data export (JSON)
        </Button>
      </div>
    </section>
  );
}
